import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetch a fresh Supabase access token at call time.
 *
 * Why this exists (Phase 69, follow-up to a beta-user 401 report):
 *   Hook-scoped closures historically captured `session?.access_token` once
 *   at render time. If the browser tab sat idle long enough for the JWT to
 *   expire (Supabase access tokens live ~1h), the captured token went stale
 *   and the backend correctly rejected upload requests with
 *   `401 Invalid or expired token`. The user only saw it as
 *   "We couldn't accept this PDF" with no obvious cause.
 *
 *   `supabase.auth.getSession()` is the supported escape hatch:
 *     - In the common case (token still valid) it returns the cached session
 *       synchronously — no network call, sub-millisecond.
 *     - If the access token is within ~30s of expiry (or already past),
 *       supabase-js transparently swaps the refresh token for a new access
 *       token (~one HTTPS round-trip) before resolving.
 *   Mirrors the pattern already used in Accounts.js, Wealth.js, and
 *   ImportInvestmentsDialog.js.
 */
async function getFreshToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
}

/**
 * useParseJobs, global hook for the async PDF parse job queue.
 *
 * Provides:
 *   • jobs: list of recent parse_jobs (newest first)
 *   • unseenDone: count for the notification bell badge
 *   • enqueue(file): uploads a PDF, returns { job_id }
 *   • refresh(): manual refresh
 *   • markSeen(jobId): mark a single job as seen
 *   • markConsumed(jobId): mark a job as imported
 *   • deleteJob(jobId): remove a job from history
 *
 * Polling cadence:
 *   • Every 4s while at least one job is queued/processing
 *   • Every 30s otherwise
 *   • Paused if the browser tab is hidden
 */
export function useParseJobs(options = {}) {
  const { noPoll = false, limit = 20 } = options;
  const { session } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [unseenDone, setUnseenDone] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  // `token` from React state is used ONLY as a "signed-in" gate for the
  // polling loop / early-returns. All actual network calls below fetch a
  // freshly-refreshed token via `getFreshToken()` to immunize against the
  // stale-closure window across token-refresh boundaries.
  const token = session?.access_token;

  const refresh = useCallback(async () => {
    const t = await getFreshToken();
    if (!t) return;
    try {
      // Phase 84.18 — the Uploaded Statements panel passes a larger limit
      // so a user with a long import history sees ALL of it (including
      // purged rows). The bell keeps the default (20) for a snappy popover.
      const r = await axios.get(`${BACKEND_URL}/api/import/jobs`, {
        headers: { Authorization: `Bearer ${t}` },
        params: { limit },
      });
      setJobs(r.data?.jobs || []);
      setUnseenDone(r.data?.unseen_done || 0);
    } catch (e) {
      // silent; we'll retry next tick
    }
  }, [limit]);

  // Only the bell needs continuous polling. Other call sites (CSVImport) just
  // need the action functions and one-shot fetches.
  const hasActive = jobs.some((j) => j.status === 'queued' || j.status === 'processing');

  // v3.6.5 fix — even when `noPoll: true` we still need ONE initial fetch on
  // mount, otherwise consumers like <UploadedStatementsPanel /> render forever
  // with an empty jobs list and show "You haven't uploaded any statements
  // yet" — even for users with dozens of real uploads visible in the bell.
  // Bell polling (below) is unaffected because it already calls refresh().
  useEffect(() => {
    if (!token) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (noPoll || !token) return undefined;
    refresh();
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      refresh();
    };
    const interval = hasActive ? 4000 : 30000;
    pollRef.current = setInterval(tick, interval);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [token, refresh, hasActive, noPoll]);

  /**
   * enqueue(file, opts), uploads a PDF and creates a parse_job.
   *
   * Phase 54 Week 4.2, uses the v2 background flow for ALL users:
   *   1. POST /api/import/v2/storage-upload-url   → signed PUT URL
   *   2. PUT  file directly to Supabase Storage   → no base64 over our API
   *   3. POST /api/import/v2/jobs                 → enqueue parse_job
   *
   * Returns: { id, status } (job_id is `id` in v2, but we alias both for
   * backward-compat with callers that read `res.job_id`).
   */
  const enqueue = useCallback(async (file, opts = {}) => {
    // Fetch the freshest possible token at the start of the upload chain.
    // If it expires partway through the multi-step flow we still re-fetch
    // before each subsequent call below.
    let t = await getFreshToken();
    if (!t) throw new Error('Not signed in');
    setLoading(true);
    try {
      // --- (1) Ask the server for a signed upload URL. -----------------------
      const sigPayload = {
        filename: file.name,
        content_type: file.type || 'application/pdf',
      };
      const sig = await axios.post(
        `${BACKEND_URL}/api/import/v2/storage-upload-url`,
        sigPayload,
        { headers: { Authorization: `Bearer ${t}` }, timeout: 15000 },
      );
      const { signed_url: signedUrl, storage_key: storageKey, token: uploadToken } = sig.data || {};
      if (!signedUrl || !storageKey) {
        throw new Error('Could not obtain a storage upload URL.');
      }

      // --- (2) PUT the PDF directly to Supabase Storage. ---------------------
      // The signed URL is single-use and short-lived (5 min). We keep a
      // 90s timeout because the PUT itself is over the network, not our API.
      const uploadHeaders = { 'Content-Type': file.type || 'application/pdf' };
      // Some Supabase signed URLs require the `upload-token` query/header.
      if (uploadToken) uploadHeaders['x-upsert'] = 'true';
      await axios.put(signedUrl, file, {
        headers: uploadHeaders,
        timeout: 90000,
        // Important: don't let axios auto-add Authorization to a non-API host.
        transformRequest: [(d) => d],
      });

      // --- (3) Enqueue the parse job. ----------------------------------------
      const jobPayload = {
        storage_key: storageKey,
        filename: file.name,
        file_size: file.size,
      };
      if (opts.account_id)  jobPayload.account_id  = opts.account_id;
      if (opts.file_hash)   jobPayload.file_hash   = opts.file_hash;
      if (opts.pdf_password) jobPayload.pdf_password = opts.pdf_password;
      // Phase 81.5.4 F5.remediation — explicit override for the
      // completed-duplicate warning. Failed/abandoned duplicates always
      // get a fresh retry server-side without needing this flag.
      if (opts.forceRetry)  jobPayload.force_retry = true;
      // Re-fetch the token in case the slow Supabase PUT crossed a refresh
      // boundary. Cheap on the cached path.
      t = (await getFreshToken()) || t;
      const res = await axios.post(`${BACKEND_URL}/api/import/v2/jobs`, jobPayload, {
        headers: { Authorization: `Bearer ${t}` },
        // Encrypted PDFs are unlocked server-side at /v2/jobs time (download
        // from Storage → pikepdf decrypt → re-upload). For large password-
        // protected statements this can take 20-40s. 60s gives us headroom
        // without locking the UI forever on a truly hung request.
        timeout: 60000,
      });
      await refresh();
      // Back-compat: callers historically read `job_id` from the legacy endpoint.
      const data = res.data || {};
      return { ...data, job_id: data.id || data.job_id };
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  const fetchJob = useCallback(async (jobId) => {
    const t = await getFreshToken();
    if (!t) return null;
    const r = await axios.get(`${BACKEND_URL}/api/import/jobs/${jobId}`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    return r.data;
  }, []);

  const markSeen = useCallback(async (jobId) => {
    const t = await getFreshToken();
    if (!t) return;
    try {
      await axios.post(`${BACKEND_URL}/api/import/jobs/${jobId}/mark-seen`, {}, {
        headers: { Authorization: `Bearer ${t}` },
      });
      await refresh();
    } catch (e) { /* ignore */ }
  }, [refresh]);

  const markConsumed = useCallback(async (jobId) => {
    const t = await getFreshToken();
    if (!t) return;
    try {
      await axios.post(`${BACKEND_URL}/api/import/jobs/${jobId}/mark-consumed`, {}, {
        headers: { Authorization: `Bearer ${t}` },
      });
      await refresh();
    } catch (e) { /* ignore */ }
  }, [refresh]);

  const deleteJob = useCallback(async (jobId) => {
    const t = await getFreshToken();
    if (!t) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/import/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      await refresh();
    } catch (e) { /* ignore */ }
  }, [refresh]);

  return { jobs, unseenDone, loading, refresh, enqueue, fetchJob, markSeen, markConsumed, deleteJob };
}
